import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { ButtonModule } from 'primeng/button';
import { TipoProductoService } from '../../../services/tipo-producto/tipo-producto.service';
import { TipoProducto } from '../../../domain/tipo-producto.domain';
import { ItemsEmisionFacadeService } from './items-emision-facade.service';
import { ItemsAireSpotComponent } from './items-aire-spot.component';
import { ItemsBannerWebComponent } from './items-banner-web.component';
import { ItemsRedesComponent } from './items-redes.component';
import { ItemsPublinotasComponent } from './items-publinotas.component';
import { ItemsCoberturaComponent } from './items-cobertura.component';
import { ItemsPntComponent } from './items-pnt.component';
import { CurrencyFormatPipe } from '../../../pipes/currency-format.pipe';

type ItemTipoCodigo = 'AIRE_SPOT' | 'WEB_BANNER' | 'REDES_PIEZA' | 'PUBLINOTA' | 'COBERTURA_EVENTO' | 'PNT';

type ItemTypeConfig = {
  codigo: ItemTipoCodigo;
  label: string;
  description: string;
};

@Component({
  selector: 'app-items-emision-section',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CurrencyFormatPipe,
    ItemsAireSpotComponent,
    ItemsBannerWebComponent,
    ItemsRedesComponent,
    ItemsPublinotasComponent,
    ItemsCoberturaComponent,
    ItemsPntComponent
  ],
  templateUrl: './items-emision-section.component.html',
  styleUrls: ['./items-emision-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ItemsEmisionSectionComponent implements OnInit, OnChanges {
  @Input({ required: true }) itemsFormArray!: FormArray;
  @Input() readOnly: boolean = false;
  @Input() itemsLoading: boolean = false;
  @Input() vigenciaDesde: Date | null = null;
  @Input() vigenciaHasta: Date | null = null;

  readonly itemTypes: ItemTypeConfig[] = [
    {
      codigo: 'AIRE_SPOT',
      label: 'Aire Spot',
      description: 'Detalle de emisiones por programa y material.'
    },
    {
      codigo: 'WEB_BANNER',
      label: 'Banner Web',
      description: 'Piezas digitales con fechas y ubicación.'
    },
    {
      codigo: 'REDES_PIEZA',
      label: 'Redes',
      description: 'Piezas en redes sociales.'
    },
    {
      codigo: 'PUBLINOTA',
      label: 'Publinotas',
      description: 'Contenido editorial patrocinado.'
    },
    {
      codigo: 'COBERTURA_EVENTO',
      label: 'Cobertura',
      description: 'Cobertura de eventos y fechas.'
    },
    {
      codigo: 'PNT',
      label: 'PNT',
      description: 'Publicidad no tradicional con texto a leer.'
    }
  ];

  activeType = signal<ItemTipoCodigo>('AIRE_SPOT');
  activeTypeConfig = computed(() => this.itemTypes.find(type => type.codigo === this.activeType())!);
  activeCount = computed(() => this.facade.countsByType()[this.activeType()]);
  activeSubtotal = computed(() => this.facade.subtotalByType()[this.activeType()]);
  activeRequiredLabels = computed(() => this.facade.getRequiredFieldLabels(this.activeType()));
  activeStatus = computed(() => {
    const count = this.activeCount();
    if (count === 0) {
      return { label: 'Sin ítems', tone: 'empty' };
    }
    const hasIncomplete = (this.facade.itemsByType()[this.activeType()] || [])
      .some(item => !this.facade.isRowComplete(item.group, this.activeType()));
    if (hasIncomplete) {
      return { label: 'Editando', tone: 'editing' };
    }
    return { label: `${count} ítems`, tone: 'ok' };
  });

  constructor(
    private tipoProductoService: TipoProductoService,
    public facade: ItemsEmisionFacadeService
  ) {}

  ngOnInit(): void {
    this.loadTipoProductos();
    this.facade.setFormArray(this.itemsFormArray);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['itemsFormArray'] && this.itemsFormArray) {
      this.facade.setFormArray(this.itemsFormArray);
    }
  }

  selectType(type: ItemTipoCodigo): void {
    this.activeType.set(type);
    if (!this.readOnly && (this.facade.countsByType()[type] || 0) === 0) {
      this.facade.addItem(type);
    }
  }

  addItem(): void {
    if (this.readOnly) {
      return;
    }
    this.facade.addItem(this.activeType());
  }

  private loadTipoProductos(): void {
    const params = new HttpParams()
      .set('active', 'true')
      .set('pagination', 'false');

    this.tipoProductoService.getAll(params as any).subscribe({
      next: (resp: any) => {
        const members = resp['hydra:member'] || resp.member || resp || [];
        const options = members as TipoProducto[];
        this.facade.setTipoProductoOptions(options);
        const aire = options.find(tipo => tipo.codigo === 'AIRE_SPOT');
        if (aire) {
          this.activeType.set('AIRE_SPOT');
        }
        if (this.itemsFormArray.length === 0 && !this.readOnly && !this.itemsLoading) {
          this.facade.addItem(this.activeType());
        }
      },
      error: () => {
        this.facade.setTipoProductoOptions([]);
      }
    });
  }
}
